import { z } from 'zod';

/**
 * SDK verbose log upload for debug sessions (BB-157). A debug session (BB-35) created with `collectSdkLogs` makes every
 * batch answer to its target installation carry a `debug` directive — only for installations of an internal group.
 * While the directive holds, the SDK keeps its own log lines in a ring buffer, redacts them on the device with
 * `SDK_LOG_REDACTIONS` and uploads them in chunks to `POST /sdk/v1/debug/logs`. Before a directive and after it ends
 * nothing is buffered.
 *
 * The SDK stops collecting, uploads what it holds once and drops the buffer when: `until` passes; a batch answer
 * comes without `debug`; or the upload answers `404 debug_session_not_active` (then it drops without uploading).
 */
export const SDK_LOG_LEVELS = ['verbose', 'debug', 'info', 'warn', 'error'] as const;
export type SdkLogLevel = (typeof SDK_LOG_LEVELS)[number];

export const SDK_DEBUG_LOG_LIMITS = {
  /** Ring buffer: the oldest lines go first when either limit is reached. */
  bufferLines: 2000,
  bufferBytes: 1024 * 1024,
  /** A longer message is cut to this many characters and ends with `…`. */
  messageChars: 2000,
  /** Lines in one upload. */
  chunkLines: 500,
  /** Upload cadence while collecting; also on app background and when collection stops. */
  uploadIntervalSeconds: 30,
} as const;

/** Part of the batch answer (`BatchResponse.debug`). */
export const SdkDebugDirective = z.strictObject({
  sessionId: z.uuid(),
  until: z.iso.datetime(),
  level: z.enum(SDK_LOG_LEVELS),
});
export type SdkDebugDirective = z.infer<typeof SdkDebugDirective>;

export const SdkLogLine = z.strictObject({
  at: z.iso.datetime(),
  level: z.enum(SDK_LOG_LEVELS),
  /** SDK component, e.g. `batch`, `cards`, `push`, `actions`. */
  tag: z.string().min(1).max(64),
  message: z.string().max(SDK_DEBUG_LOG_LIMITS.messageChars),
});
export type SdkLogLine = z.infer<typeof SdkLogLine>;

/**
 * `POST /sdk/v1/debug/logs`, authenticated like the batch. The body may be gzip (`Content-Encoding: gzip`), at most
 * 256 KB on the wire. `chunkIndex` counts uploads of one installation in one debug session from 0. A retry of a chunk
 * (same index and lines) answers `accepted: false` and the SDK moves on; the same index with other lines (the app was
 * restarted and counts from 0 again) is stored. `X-BB-User-Token` is not checked: only internal-group devices of an
 * active session may upload.
 * Answers: `202 { accepted }`; `404 debug_session_not_active` (no active session with log collection for this
 * installation); `400 validation_failed`; `413 payload_too_large`; `429 rate_limited` (20 a minute per installation).
 * SDKs: 2xx drops the sent lines; 400 / 413 drop the chunk; any 404 stops collecting; network errors, 429 and 5xx keep
 * the lines for the next attempt.
 */
export const SdkDebugLogUpload = z.strictObject({
  schemaVersion: z.literal(1),
  installationId: z.uuid(),
  debugSessionId: z.uuid(),
  chunkIndex: z.number().int().min(0).max(100_000),
  lines: z.array(SdkLogLine).min(1).max(SDK_DEBUG_LOG_LIMITS.chunkLines),
});
export type SdkDebugLogUpload = z.infer<typeof SdkDebugLogUpload>;
export const SdkDebugLogUploadResponse = z.strictObject({ accepted: z.boolean() });

/**
 * On-device redaction, applied to every message in this order before it enters the buffer (Java and ICU regex
 * syntax; `$1` refers to a group). SDKs log attribute names only, never values; the last rule is a safety net that
 * removes the first word of an `attribute <name>=<value>` value. Phone numbers are recognised only with a leading `+`.
 * ICU's `\w`, `\d` and `\s` also match non-ASCII characters, so iOS may redact slightly more than Android. Message
 * length is counted in UTF-16 units (as JavaScript does). Shared cases: `fixtures/debug-logs/redaction.json`.
 */
export const SDK_LOG_REDACTIONS: ReadonlyArray<{ name: string; pattern: string; replacement: string }> = [
  { name: 'email', pattern: '[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}', replacement: '<email>' },
  { name: 'bearer', pattern: '(?i)bearer\\s+[A-Za-z0-9._~+/=-]+', replacement: 'Bearer <token>' },
  { name: 'secret_field', pattern: '(?i)(token|api[_-]?key|secret|password|authorization)(["\']?\\s*[:=]\\s*["\']?)(?!bearer\\s)[^\\s"\',;}&]+', replacement: '$1$2<redacted>' },
  { name: 'long_token', pattern: '[A-Za-z0-9_:-]{40,}', replacement: '<token>' },
  { name: 'phone', pattern: '\\+\\d[\\d\\s().-]{7,}\\d', replacement: '<phone>' },
  { name: 'attribute_value', pattern: '(attribute\\s+[\\w.$-]+\\s*=\\s*)[^\\s,;]+', replacement: '$1<redacted>' },
];

/** The reference implementation of `SDK_LOG_REDACTIONS` (the SDKs mirror it; the fixtures pin the behaviour). */
export function redactSdkLogMessage(message: string): string {
  return SDK_LOG_REDACTIONS.reduce((text, rule) => {
    const insensitive = rule.pattern.startsWith('(?i)');
    const re = new RegExp(insensitive ? rule.pattern.slice(4) : rule.pattern, insensitive ? 'gi' : 'g');
    return text.replace(re, rule.replacement);
  }, message);
}
